/**
 * Deserializes .ipynb cell outputs into CellOutput format.
 *
 * Handles Jam SQL structured outputs (application/vnd.jam-sql.result+json),
 * stream outputs (messages), and error outputs.
 */

import type { CellOutput } from '../types/notebookTypes';
import type { QueryResultSet, QueryMessage } from '../../core/types/workspaceTypes';

// ============================================================================
// Types
// ============================================================================

export type RawIpynbOutput = {
  output_type?: string;
  data?: Record<string, unknown>;
  text?: string | string[];
  name?: string;
  metadata?: Record<string, unknown>;
  ename?: string;
  evalue?: string;
  traceback?: string[];
  execution_count?: number | null;
  [key: string]: unknown;
};

// ============================================================================
// Output Deserialization
// ============================================================================

/** Join source lines — accepts both string and string[]. */
function joinText(source: string | string[] | undefined): string {
  if (!source) return '';
  if (typeof source === 'string') return source;
  return source.join('');
}

export function deserializeCellOutputs(rawOutputs?: RawIpynbOutput[]): CellOutput | undefined {
  if (!rawOutputs || rawOutputs.length === 0) return undefined;

  const resultSets: QueryResultSet[] = [];
  const messages: QueryMessage[] = [];
  let durationMs: number | undefined;
  let totalRowCount: number | undefined;

  for (const output of rawOutputs) {
    if (output.output_type === 'execute_result' || output.output_type === 'display_data') {
      deserializeExecuteResult(output, resultSets, durationMs, totalRowCount, (d, t) => {
        if (d !== undefined) durationMs = d;
        if (t !== undefined) totalRowCount = t;
      });
    }

    if (output.output_type === 'stream') {
      deserializeStreamOutput(output, messages);
    }

    if (output.output_type === 'error') {
      deserializeErrorOutput(output, messages);
    }
  }

  if (resultSets.length === 0 && messages.length === 0) return undefined;

  return {
    resultSets,
    messages,
    ...(durationMs !== undefined ? { durationMs } : {}),
    ...(totalRowCount !== undefined ? { totalRowCount } : {}),
  };
}

// ============================================================================
// Helpers
// ============================================================================

function deserializeExecuteResult(
  output: RawIpynbOutput,
  resultSets: QueryResultSet[],
  _durationMs: number | undefined,
  _totalRowCount: number | undefined,
  setMeta: (d: number | undefined, t: number | undefined) => void
): void {
  const jamResult = output.data?.['application/vnd.jam-sql.result+json'];
  if (!jamResult || typeof jamResult !== 'object') return;

  const typed = jamResult as {
    /** `id` is present only for a duplicate-named or unnamed column (see the serializer). */
    columns?: Array<{ id?: string; name: string; dataType: string }>;
    rows?: Array<Record<string, unknown>>;
    totalRowCount?: number;
    durationMs?: number;
  };

  if (typed.columns && typed.rows) {
    resultSets.push({ columns: typed.columns, rows: typed.rows });
    setMeta(typed.durationMs, typed.totalRowCount);
  }
}

function deserializeStreamOutput(output: RawIpynbOutput, messages: QueryMessage[]): void {
  const textLines = joinText(output.text);
  const lines = textLines.split('\n').filter(Boolean);

  for (const line of lines) {
    const match = line.match(/^\[(INFO|WARNING|ERROR)\]\s*(.*)$/);
    if (match) {
      messages.push({
        id: crypto.randomUUID(),
        type: match[1].toLowerCase() as 'info' | 'warning' | 'error',
        content: match[2],
        timestamp: new Date(),
      });
    } else {
      messages.push({
        id: crypto.randomUUID(),
        type: output.name === 'stderr' ? 'error' : 'info',
        content: line,
        timestamp: new Date(),
      });
    }
  }
}

function deserializeErrorOutput(output: RawIpynbOutput, messages: QueryMessage[]): void {
  const traceback = Array.isArray(output.traceback) ? output.traceback.join('\n') : '';
  const errorMsg = output.ename || 'Error';
  const errorValue = output.evalue || '';
  messages.push({
    id: crypto.randomUUID(),
    type: 'error',
    content: `${errorMsg}: ${errorValue}${traceback ? '\n' + traceback : ''}`,
    timestamp: new Date(),
  });
}
